import {
  meterFlowKey,
  meterStackKey,
  toIntervalMeterSource,
} from "./interval-utils"
import type {
  IntervalItem,
  IntervalMeterIdentity,
  IntervalStackedDataPoint,
  IntervalStackedSeries,
} from "./interval.types"

interface StackedSeriesDraft {
  flowDirection: IntervalItem["flow_direction"]
  key: string
  meterLabel: string
  source: string
}

interface StackedSeriesResult {
  data: IntervalStackedDataPoint[]
  series: IntervalStackedSeries[]
}

const signedChartValue = (item: IntervalItem): number => {
  const parsed = Number(item.value)
  const magnitude = Number.isFinite(parsed) ? parsed : 0
  if (item.flow_direction !== "export") {
    return magnitude
  }
  // -Math.abs(0) is -0, which Intl can render as -0.00
  if (magnitude === 0) {
    return 0
  }
  return -Math.abs(magnitude)
}

const compareStackedSeries = (
  left: StackedSeriesDraft,
  right: StackedSeriesDraft
): number => {
  const meterCmp = left.meterLabel.localeCompare(right.meterLabel, undefined, {
    numeric: true,
  })
  if (meterCmp !== 0) {
    return meterCmp
  }
  if (left.flowDirection === right.flowDirection) {
    return 0
  }
  return left.flowDirection === "import" ? -1 : 1
}

export const toStackedSeries = (
  items: IntervalItem[] | undefined,
  meters: IntervalMeterIdentity[]
): StackedSeriesResult => {
  const metersById = new Map(meters.map((meter) => [meter.id, meter]))
  const seriesByKey = new Map<string, StackedSeriesDraft>()
  const valuesByStart = new Map<string, Record<string, number>>()

  for (const item of items ?? []) {
    const meterId = meterStackKey(item.meter_id)
    const key = meterFlowKey(item.meter_id, item.flow_direction)
    if (!seriesByKey.has(key)) {
      const meter = metersById.get(meterId)
      seriesByKey.set(key, {
        flowDirection: item.flow_direction,
        key,
        meterLabel: meter?.label ?? meterId,
        source: toIntervalMeterSource(meter, item.flow_direction),
      })
    }

    const values = valuesByStart.get(item.start_datetime) ?? {}
    values[key] = (values[key] ?? 0) + signedChartValue(item)
    valuesByStart.set(item.start_datetime, values)
  }

  const series = [...seriesByKey.values()]
    .sort(compareStackedSeries)
    .map((item) => ({
      flowDirection: item.flowDirection,
      key: item.key,
      label: item.meterLabel,
      source: item.source,
    }))
  const data = [...valuesByStart.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([startDatetime, values]) => ({
      ...values,
      xaxis: startDatetime,
    }))

  return { data, series }
}

const groupStackedDataPoint = (
  point: IntervalStackedDataPoint,
  series: IntervalStackedSeries[]
): IntervalStackedDataPoint => {
  const grouped: IntervalStackedDataPoint = { xaxis: point.xaxis }
  for (const item of series) {
    const value = point[item.key]
    if (typeof value !== "number") {
      continue
    }
    const current = grouped[item.source]
    grouped[item.source] = (typeof current === "number" ? current : 0) + value
  }
  return grouped
}

export const groupStackedSeriesBySource = (
  data: IntervalStackedDataPoint[],
  series: IntervalStackedSeries[]
): StackedSeriesResult => {
  const groupedSeries: IntervalStackedSeries[] = []
  const seen = new Set<string>()
  for (const item of series) {
    if (seen.has(item.source)) {
      continue
    }
    seen.add(item.source)
    groupedSeries.push({
      flowDirection: item.flowDirection,
      key: item.source,
      label: item.source,
      source: item.source,
    })
  }

  return {
    data: data.map((point) => groupStackedDataPoint(point, series)),
    series: groupedSeries,
  }
}
